import axios from 'axios';
import { getKeycloak } from './keycloak';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api';

// Create axios instance with auth interceptor
const cmi5ApiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
cmi5ApiClient.interceptors.request.use(async (config) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
    return config;
  }

  // Fallback to Keycloak instance
  try {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  } catch (error) {
    console.warn('Could not get auth token for CMI5 API');
  }

  return config;
});

export interface RC5AU {
  id: string;
  title: string;
  description?: string;
  rangeosScenarioName?: string;
  rangeosScenarioUUID?: string;
  mappedScenarioId?: string;
  mappedScenarioName?: string;
  // Active AU mappings from RangeOS API
  mappedScenarios?: string[];
  mappingName?: string;
  // Block information
  blockName?: string;
  blockDescription?: string;
}

export interface RC5Block {
  blockName: string;
  blockDescription?: string;
  aus: RC5AU[];
}

export interface RC5Config {
  au?: RC5AU[];  // Flat list (legacy/backward compatibility)
  blocks?: RC5Block[];  // Structured by blocks (preferred)
  [key: string]: any;
}

export const cmi5ConfigService = {
  /**
   * Get RC5 config for a CMI5 activity
   */
  async getRC5Config(
    sourceId: string,
    tenantId: string,
    courseId: string
  ): Promise<RC5Config> {
    const response = await cmi5ApiClient.get(
      `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/rc5`
    );
    return response.data;
  },

  /**
   * List all CMI5 activities for a Moodle source
   */
  async listCMI5Activities(sourceId: string): Promise<Array<{
    tenantId: string;
    courseId: string;
    activityId: number;
    activityName: string;
    cmi5activityid?: string;
    moodleCourseId?: number;
  }>> {
    const response = await cmi5ApiClient.get(
      `/moodle/sources/${sourceId}/cmi5-activities`
    );
    return response.data;
  },

  /**
   * Configure a CMI5 activity with scenario mappings
   */
  async configureCMI5Activity(
    sourceId: string,
    tenantId: string,
    courseId: string,
    rangeosApiEndpointId: string,
    scenarioMappings: Array<{
      auId: string;
      scenarioId: string;
      scenarioName: string;
    }>
  ): Promise<{
    success: boolean;
    configured: string[];
    failed: string[];
    message: string;
  }> {
    const response = await cmi5ApiClient.post(
      `/moodle/sources/${sourceId}/configure-cmi5-activity`,
      {
        tenantId,
        courseId,
        rangeosApiEndpointId,
        scenarioMappings,
      }
    );
    return response.data;
  },

  /**
   * Reconfigure an existing CMI5 activity
   */
  async reconfigureCMI5Activity(
    sourceId: string,
    tenantId: string,
    courseId: string,
    rangeosApiEndpointId: string,
    scenarioMappings: Array<{
      auId: string;
      scenarioId: string;
      scenarioName: string;
    }>
  ): Promise<{
    success: boolean;
    configured: string[];
    failed: string[];
    message: string;
  }> {
    const response = await cmi5ApiClient.post(
      `/moodle/sources/${sourceId}/reconfigure-cmi5-activity`,
      {
        tenantId,
        courseId,
        rangeosApiEndpointId,
        scenarioMappings,
      }
    );
    return response.data;
  },

  /**
   * Get cfg.json for a CMI5 activity.
   * If block is provided, reads from that specific block directory.
   */
  async getCfgJson(
    sourceId: string,
    tenantId: string,
    courseId: string,
    block?: string
  ): Promise<any> {
    const url = block
      ? `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/blocks/${encodeURIComponent(block)}/cfg`
      : `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/cfg`;
    const response = await cmi5ApiClient.get(url);
    return response.data;
  },

  /**
   * Update cfg.json for a CMI5 activity.
   * If block is provided, writes only within that block directory.
   */
  async updateCfgJson(
    sourceId: string,
    tenantId: string,
    courseId: string,
    cfgData: any,
    block?: string
  ): Promise<{ success: boolean; message: string }> {
    const url = block
      ? `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/blocks/${encodeURIComponent(block)}/cfg`
      : `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/cfg`;
    const response = await cmi5ApiClient.put(url, cfgData);
    return response.data;
  },

  /**
   * Validate CMI5 activities comprehensively
   */
  async validateActivities(
    sourceId: string,
    activities: Array<{
      activityId: number;
      activityName: string;
      tenantId: string;
      courseId: string;
      moodleCourseId: number;
      cmi5activityid?: string;
    }>,
    environmentId?: string
  ): Promise<any[]> {
    const response = await cmi5ApiClient.post(
      `/moodle/sources/${sourceId}/cmi5-activities/validate`,
      {
        activities,
        environmentId,
      }
    );
    return response.data;
  },
};
